import { Card, Dialog, Div, Hr, Icon, Link, P, Space, Span, Tag, Tooltip, VisuallyHidden } from '@dnb/eufemia';
import { padlock as PadlockIcon, view as ViewIcon } from '@dnb/eufemia/icons';
import type { JSX } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ApiFavouriteButton from '@/components/ApiFavouriteButton';
import useAuth from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';
import ApiStageBadge from '@/pages/profile/components/ApiStageBadge';

import { type ApiItem, getClassification, getCurrentReleases } from '../../apiUtils';
import { QuickLook } from '../../QuickLook';

import style from './ApiCard.module.css';

const EXTERNAL_CLASSIFICATIONS = new Set(['partner', 'public']);

function visibilityReason(api: ApiItem) {
  const classification = getClassification(api);

  if (!('organization' in api)) {
    return null;
  }

  if (api.organization && EXTERNAL_CLASSIFICATIONS.has(classification)) {
    return `${api.organization.name} organization`;
  }

  if (!('teams' in api)) {
    return null;
  }

  if (api.teams.length > 0) {
    if (api.organization?.name === 'DNB') {
      return null;
    }
    return `${api.teams[0]!.name} team`;
  }

  return null;
}

interface ApiCardProps {
  api: ApiItem;
  onApiFavouriteChange: (apiId: string, isFavorite: boolean) => void;
}

export default function ApiCard({ api, onApiFavouriteChange }: ApiCardProps): JSX.Element {
  const visibleBecause = visibilityReason(api);
  const { user } = useUser();
  const { isInternal } = useAuth();
  const classification = getClassification(api);
  const tags = api.tags;
  const currentReleases = getCurrentReleases(api);

  return (
    <Card bottom="small" className={style['ApiCard']} stretch>
      <Space className={style['TopSection']}>
        <Div bottom="x-small" className="flex gap-3 items-center">
          <Link element={RouterLink} to={`/documentation/${api.slug}/@default/@latest`}>
            {api.name}
          </Link>
          <ApiStageBadge compact stage={api.stage} />
          {visibleBecause && (
            <Tooltip
              hideDelay={0}
              noAnimation
              showDelay={100}
              size="large"
              targetElement={
                <Span right="x-small">
                  <Icon icon={PadlockIcon} size="medium" />
                </Span>
              }
            >
              <span>You can see this API because you are a member of the {visibleBecause}</span>
            </Tooltip>
          )}
        </Div>

        {user && (
          <ApiFavouriteButton
            apiId={api.id}
            isFavorite={api.isFavorite}
            onChange={(apiId, isFavorite) => onApiFavouriteChange(apiId, isFavorite)}
          />
        )}
      </Space>

      {currentReleases.length > 0 ? (
        <>
          <VisuallyHidden>Current releases</VisuallyHidden>
          <Space className={style['Versions']}>
            {currentReleases.map((release) => (
              <Link
                className={style['Version']}
                element={RouterLink}
                key={release.version}
                to={`/documentation/${api.slug}/@default/${release.version}`}
              >
                {release.version}
              </Link>
            ))}
          </Space>
        </>
      ) : (
        api.currentVersion && (
          <>
            <VisuallyHidden>Current version</VisuallyHidden>
            <Span className={style['Version']}>{api.currentVersion}</Span>
          </>
        )
      )}

      <P bottom="small" top="x-small">
        {api.description}
      </P>

      {((classification && isInternal) || tags.length > 0) && (
        <Tag.Group bottom="x-small" label="API classification and tags" top="xx-small">
          {classification && isInternal && (
            <Tooltip
              targetElement={
                <Tag style={{ backgroundColor: 'var(--color-mint-green-50)', textTransform: 'capitalize' }}>
                  {classification}
                </Tag>
              }
            >
              Classification
            </Tooltip>
          )}
          {tags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Tag.Group>
      )}

      <Hr className={style['Divider']} />

      <div className={style['ButtonsContainer']}>
        <Dialog
          maxWidth="60rem"
          triggerAttributes={{
            text: 'Quick view',
            variant: 'tertiary',
            icon: ViewIcon,
          }}
        >
          <QuickLook api={api} />
        </Dialog>
      </div>
    </Card>
  );
}
